import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { MultiSelect, Select } from './form'
import { EXTRA_LONG_POLL_INTERVAL_MS, SSR } from '@/lib/constants'
import { ACTIVE_SUBS, SUB_FULL } from '@/fragments/subs'
import { useApolloClient, useQuery } from '@apollo/client/react'
import styles from './sub-select.module.css'
import { useMe } from './me'
import { useShowModal } from './modal'
import { TerritoryInfo } from './territory-header'
import { subNames, subNamesFromSlug } from '@/lib/subs'

export function SubSelectInitial ({ item, subs }) {
  const router = useRouter()
  const names = item?.subNames || subNames(subs) || subNamesFromSlug(router.query.sub)

  // Turf repost (2026-09-24): creation is single-turf — preselect only a
  // single page-context turf (e.g. /~monero/post), never the whole sub list.
  return {
    subNames: item?.subNames || (names?.length === 1 ? names : [])
  }
}

const DEFAULT_PREPEND_SUBS = []
const DEFAULT_APPEND_SUBS = []
const DEFAULT_FILTER_SUBS = () => true

export function useSubs ({ prependSubs = DEFAULT_PREPEND_SUBS, sub, filterSubs = DEFAULT_FILTER_SUBS, appendSubs = DEFAULT_APPEND_SUBS }) {
  const { data, refetch } = useQuery(ACTIVE_SUBS, SSR
    ? {}
    : {
        pollInterval: EXTRA_LONG_POLL_INTERVAL_MS,
        nextFetchPolicy: 'cache-and-network'
      })

  const { me } = useMe()

  useEffect(() => {
    refetch()
  }, [me?.privates?.nsfwMode])

  const [subs, setSubs] = useState([
    ...prependSubs.filter(s => s !== sub),
    ...(sub ? [sub] : []),
    ...appendSubs.filter(s => s !== sub)])

  useEffect(() => {
    if (!data) return

    const joined = data.activeSubs.filter(filterSubs).filter(s => !s.meMuteSub).map(s => s.name)
    const muted = data.activeSubs.filter(filterSubs).filter(s => s.meMuteSub).map(s => s.name)
    const mutedSection = muted.length ? [{ label: 'muted', items: muted }] : []
    setSubs([
      ...prependSubs,
      ...joined,
      ...mutedSection,
      ...appendSubs])
  }, [data])

  return subs
}

// navigate to a turf (or home) from wherever we are, keeping sub-aware routes
export function useGoToSub () {
  const router = useRouter()
  return (value) => {
    const sub = ['frontpage', 'pick territory'].includes(value) ? undefined : value
    if (sub === 'create') {
      router.push('/territory')
      return
    }

    let asPath
    // are we currently in a sub (ie not home)
    if (router.query.sub) {
      // are we going to a sub or home?
      const subReplace = sub ? `/~${sub}` : ''

      // if we are going to a sub, replace the current sub with the new one
      asPath = router.asPath.replace(`/~${router.query.sub}`, subReplace)
      // if we're going to home, just go there directly
      if (asPath === '') {
        router.push('/')
        return
      }
    } else {
      // we're currently on the home sub
      // if in /top/cowboys, /top/territories, or /top/stashers
      // and a territory is selected, go to /~sub/top/posts/day
      if (router.pathname.startsWith('/~/top/cowboys')) {
        router.push(sub ? `/~${sub}/top/posts/day` : '/top/cowboys')
        return
      } else if (router.pathname.startsWith('/~/top/stashers')) {
        router.push(sub ? `/~${sub}/top/posts/day` : 'top/stashers/day')
        return
      } else if (router.pathname.startsWith('/~/top/territories')) {
        router.push(sub ? `/~${sub}/top/posts/day` : '/top/territories/day')
        return
      } else if (router.pathname.startsWith('/~')) {
        // are we in a sub aware route?
        // if we are, go to the same path but in the sub
        asPath = `/~${sub}` + router.asPath
      } else {
        // otherwise, just go to the sub
        router.push(sub ? `/~${sub}` : '/')
        return
      }
    }
    const query = {
      ...router.query,
      sub
    }
    delete query.nodata
    router.push({
      pathname: router.pathname,
      query
    }, asPath)
  }
}

export default function SubSelect ({ prependSubs, sub, onChange, size, appendSubs, filterSubs, className, ...props }) {
  const goToSub = useGoToSub()
  const subs = useSubs({ prependSubs, sub, filterSubs, appendSubs })
  const valueProps = props.noForm
    ? {
        value: sub
      }
    : {
        overrideValue: sub
      }

  // If logged out user directly visits a nsfw sub, subs will not contain `sub`, so manually add it
  // to display the correct sub name in the sub selector
  const subItems = !sub || subs.find((s) => s === sub) ? subs : [sub].concat(subs)

  return (
    <Select
      onChange={onChange || ((_, e) => {
        goToSub(e.target.value)
      })}
      name='sub'
      size='sm'
      {...valueProps}
      {...props}
      className={`${className} ${styles.subSelect} ${size === 'large' ? styles.subSelectLarge : size === 'medium' ? styles.subSelectMedium : ''}`}
      items={subItems}
    />
  )
}

export function SubMultiSelect ({ prependSubs, subs, onChange, size, appendSubs, filterSubs, className, single = false, ...props }) {
  const router = useRouter()
  const client = useApolloClient()
  const activeSubs = useSubs({ prependSubs, subs, filterSubs, appendSubs })
  const valueProps = props.noForm
    ? {
        value: subs
      }
    : {
        // an EMPTY list must not act as an override: MultiSelect's sync
        // effect would clobber formik initialValues with [] on mount — e.g.
        // a server draft's turf prefill on the unscoped /post route, where
        // the form's subs prop is [] (2026-09-23 draft-post bug)
        overrideValue: subs?.length ? subs : undefined
      }

  const showModal = useShowModal()

  const handleTerritoryClick = async (subName) => {
    try {
      const { data } = await client.query({
        query: SUB_FULL,
        variables: { sub: subName }
      })
      if (data?.sub) {
        showModal(() => <TerritoryInfo sub={data.sub} includeLink />)
      }
    } catch (err) {
      console.error(err)
    }
  }

  // If logged out user directly visits a nsfw sub, subs will not contain `sub`, so manually add it
  // to display the correct sub name in the sub selector
  // const subItems = !sub || subs.find((s) => s === sub) ? subs : [sub].concat(subs)

  return (
    <MultiSelect
      id='subNames'
      onValueClick={handleTerritoryClick}
      onChange={onChange || ((_, e) => {
        // NOTE: a lot of this is not used yet, because this component is only used in PostForm,
        // but we'll keep it here for future use
        if (e.length === 1 && e.includes('create')) {
          router.push('/territory')
          return
        }
        const sub = e.length ? e.join('~') : undefined

        let asPath
        // are we currently in a sub (ie not home)
        if (router.query.sub) {
          // are we going to a sub or home?
          const subReplace = sub ? `/~${sub}` : ''

          // if we are going to a sub, replace the current sub with the new one
          asPath = router.asPath.replace(`/~${router.query.sub}`, subReplace)
          // if we're going to home, just go there directly
          if (asPath === '') {
            router.push('/')
            return
          }
        } else {
          // we're currently on the home sub
          // if in /top/cowboys, /top/territories, or /top/stashers
          // and a territory is selected, go to /~sub/top/posts/day
          if (router.pathname.startsWith('/~/top/cowboys')) {
            router.push(sub ? `/~${sub}/top/posts/day` : '/top/cowboys')
            return
          } else if (router.pathname.startsWith('/~/top/stashers')) {
            router.push(sub ? `/~${sub}/top/posts/day` : 'top/stashers/day')
            return
          } else if (router.pathname.startsWith('/~/top/territories')) {
            router.push(sub ? `/~${sub}/top/posts/day` : '/top/territories/day')
            return
          } else if (router.pathname.startsWith('/~')) {
            // are we in a sub aware route?
            // if we are, go to the same path but in the sub. An empty
            // selection must not build a /~undefined path (that 404s): on an
            // already-unscoped path there is nothing to navigate to and the
            // form field has already been cleared, so leave the URL alone
            // (2026-09-23 draft/turf bug).
            if (!sub) return
            asPath = `/~${sub}` + router.asPath
          } else {
            // otherwise, just go to the sub
            router.push(sub ? `/~${sub}` : '/')
            return
          }
        }
        const query = {
          ...router.query,
          sub
        }
        delete query.nodata
        router.push({
          pathname: router.pathname,
          query
        }, asPath)
      })}
      name='subNames'
      size='md'
      single={single}
      {...valueProps}
      {...props}
      className={`${className} ${styles.subSelect} ${size === 'large' ? styles.subSelectLarge : size === 'medium' ? styles.subSelectMedium : ''}`}
      items={activeSubs}
    />
  )
}
